import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { useAppSelector } from 'src/utils/Hooks';
import { History, Location } from 'history';
import { useDispatch } from 'react-redux';
import filtersActions from 'src/store/modules/filters/actions';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { ProductGrowthFilterModalHeader } from 'src/components/BeaconV2Components/ProductGrowthModal/ModalComponents/ProductGrowthFilterModal';
import {
  AppFilterModalFooter,
  FilterGroup,
  sortByUnitsSold,
  sortBySelected,
  sortSelectedThenAlpha,
  sortBySelectionOrder
} from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterModal';
import RetailPriceFilterSection from 'src/components/BeaconV2Components/common/AppFilterButton/RetailPriceFilterSection';
import BusinessUnitFilterGroup from 'src/components/AtlasV2Components/AtlasFilterModal/AtlasBusinessUnitFilterGroup';
import {
  FilterKey,
  generateFilterUrlString,
  validMinMaxRetailPrice
} from 'src/components/BeaconV2Components/common/AppFilterButton/utils';
import { useMainEntityId, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';

const GenericStyledDialogue = styled(Dialog)({
  '& .MuiPaper-root': {
    width: '752px',
    height: '641px',
    boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.06)',
    border: 'solid 1px #dedede',
    backgroundColor: '#fff',
    borderRadius: '8px'
  }
});

interface AtlasFilterModalProps {
  open: boolean;
  handleClose: () => void;
  history: History;
  location: Location;
}

/**
 * Atlas-only Filter Modal. Visual parity with AppFilterModal, but without Beacon-specific filters.
 * Business Unit selection supports parent grouping to prevent duplicate segments across parents.
 */
const AtlasFilterModalInner = ({ history, handleClose }: { history: History; handleClose: () => void }) => {
  const filters = useAppSelector((state) => state.filters);
  const segments = useAppSelector((state) => state.segments);
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);
  const dispatch = useDispatch();
  const mainEntityType = useMainEntityType();
  const mainEntityId = useMainEntityId();
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);

  const { savedSearchesById } = segments;

  const [selectedBusinessUnitFieldOption, setSelectedBusinessFieldOption] = useState(new Set(filters.businessUnits));
  // Track selection order to maintain temporal ordering
  const [selectionOrder, setSelectionOrder] = useState<string[]>(
    () => (filters as any)?.atlasSelectionOrder || filters.businessUnits || []
  );
  // Track where a segment/BU was selected from (top-level BU id) to preserve grouping
  const [selectedSegmentParentMap, setSelectedSegmentParentMap] = useState<Map<string, string>>(() => {
    const pairs = ((filters as any)?.atlasSegmentParentMap || []) as { s: string; p: string }[];
    return new Map(pairs.map(({ s, p }) => [s, p]));
  });
  const [selectedBUParentMap, setSelectedBUParentMap] = useState<Map<string, string>>(() => {
    const pairs = ((filters as any)?.atlasBusinessUnitParentMap || []) as { b: string; p: string }[];
    return new Map(pairs.map(({ b, p }) => [b, p]));
  });
  const [retailPriceRange, setRetailPriceRange] = useState<{ min?: string; max?: string }[]>([
    { min: (filters?.retailPrice as any)?.minValue || '' },
    { max: (filters?.retailPrice as any)?.maxValue || '' }
  ]);

  // Category/Subcategory/Segment state mirroring AppFilterModal
  const defaultCategories = useMemo(() => {
    return categories
      .map((category: any) => ({
        ...category,
        isSelected: !!filters.category?.find((c: any) => String(c.id) === String(category.id))
      }))
      .sort(sortByUnitsSold)
      .sort(sortBySelected);
  }, [categories, filters.category]);
  const [availableCategories, setAvailableCategories] = useState(defaultCategories);

  const defaultSubcategories = useMemo(() => {
    return subCategories
      .map((subcategory: any) => ({
        ...subcategory,
        isSelected: !!filters.subcategory?.find((sc: any) => String(sc.id) === String(subcategory.id))
      }))
      .sort(sortByUnitsSold)
      .sort(sortBySelected);
  }, [subCategories, filters?.subcategory]);
  const [availableSubcategories, setAvailableSubcategories] = useState(defaultSubcategories);

  const defaultSegments = useMemo(() => {
    const all = [...(segments.mySegments || []), ...(segments.teamSegments || [])];
    return all
      .map((segment: any) => ({
        ...segment,
        isSelected: filters.segment ? (filters.segment as any).id === String(segment.id) : false
      }))
      .sort(sortSelectedThenAlpha);
  }, [segments?.mySegments, segments?.teamSegments, filters?.segment]);
  const [availableSegments, setAvailableSegments] = useState(defaultSegments);

  const categoryOptions = useMemo(
    () =>
      availableCategories
        .filter((c) => c.id !== 0)
        .sort(sortByUnitsSold)
        .sort(sortBySelected),
    [availableCategories]
  );

  const subCategoryOptions = useMemo(() => {
    let selectedCategories = categoryOptions.filter((c: any) => c.isSelected);

    // If mainEntity is a category, auto-filter to that category
    if (mainEntity?.type === 'category' && String(mainEntity.id) !== '0') {
      selectedCategories = categoryOptions.filter((c: any) => String(c.id) === String(mainEntity.id));
    }

    if (selectedCategories.length > 0) {
      // Get all valid subcategory IDs from selected categories
      const validSubcategoryIds = selectedCategories.flatMap((cat: any) =>
        (cat.childEntities || []).map((child: any) => String(child.id))
      );

      // Filter and sort subcategories
      return availableSubcategories
        .filter((s: any) => validSubcategoryIds.includes(String(s.id)))
        .sort(sortByUnitsSold)
        .sort(sortBySelected);
    } else {
      // No categories selected - show all subcategories
      return [...availableSubcategories].sort(sortByUnitsSold).sort(sortBySelected);
    }
  }, [availableSubcategories, categoryOptions, mainEntity]);

  const segmentOptions = useMemo(() => [...availableSegments].sort(sortSelectedThenAlpha), [availableSegments]);

  const onSelectCategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value; // Keep as string
    const newCategories = availableCategories.map((c) =>
      String(c.id) === id ? { ...c, isSelected: !c.isSelected } : c
    );
    setAvailableCategories(newCategories);

    // Update subcategories based on selected categories
    const selectedCategories = newCategories.filter((c: any) => c.isSelected);

    if (selectedCategories.length < 1) {
      // No categories selected - reset subcategories to default
      setAvailableSubcategories(defaultSubcategories.map((s: any) => ({ ...s, isSelected: false })));
    } else {
      // Filter subcategories to only those belonging to selected categories
      const validSubcategoryIds = selectedCategories.flatMap((cat: any) =>
        (cat.childEntities || []).map((child: any) => String(child.id))
      );
      const validSubcategories = availableSubcategories.map((s: any) => ({
        ...s,
        isSelected: validSubcategoryIds.includes(String(s.id)) ? s.isSelected : false
      }));
      setAvailableSubcategories(validSubcategories);
    }
  };

  const onSelectSubcategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = e.target.value; // Keep as string
    setAvailableSubcategories((prev: any[]) =>
      prev.map((s: any) => (String(s.id) === id ? { ...s, isSelected: !s.isSelected } : s))
    );
  };

  const onSelectSegment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = String(e.target.value);
    setAvailableSegments((prev: any[]) => prev.map((s: any) => ({ ...s, isSelected: String(s.id) === id })));
  };

  const topLevelBusinessUnitsOptions = useMemo(() => {
    if (!savedSearchesById) return [] as any[];
    const items = Array.from(savedSearchesById.values()).filter((entity: any) => {
      return entity?.type === 'businessunit' && entity?.isTopLevel && entity?.children?.length && entity?.displayName;
    });
    return items;
  }, [savedSearchesById]);

  const onBusinessUnitSelect = (e: React.ChangeEvent<HTMLInputElement>, parentTopLevelId?: string) => {
    const id = e.target.value as string;
    const checked = e.target.checked;
    const next = new Set(selectedBusinessUnitFieldOption);
    if (checked) {
      next.add(id);
      // Track selection order
      setSelectionOrder((prev) => [...prev, id]);
      // Record parent grouping for the newly selected id
      const entity = savedSearchesById.get(id as string) as any;
      if (entity?.type === 'segment' && parentTopLevelId) {
        setSelectedSegmentParentMap((prev) => {
          const copy = new Map(prev);
          copy.set(id, parentTopLevelId);
          return copy;
        });
      }
      if (entity?.type === 'businessunit' && parentTopLevelId) {
        setSelectedBUParentMap((prev) => {
          const copy = new Map(prev);
          copy.set(id, parentTopLevelId);
          return copy;
        });
      }
    } else {
      next.delete(id);
      // Remove from selection order
      setSelectionOrder((prev) => prev.filter((itemId) => itemId !== id));
      // Remove mapping when deselected
      setSelectedSegmentParentMap((prev) => {
        const copy = new Map(prev);
        copy.delete(id);
        return copy;
      });
      setSelectedBUParentMap((prev) => {
        const copy = new Map(prev);
        copy.delete(id);
        return copy;
      });
    }
    setSelectedBusinessFieldOption(next);
  };

  const onChangeRetailPriceFilter = (event: React.ChangeEvent<HTMLInputElement>, type: 'min' | 'max') => {
    const value = event.target.value;
    const next: { min?: string; max?: string }[] = [...retailPriceRange];
    if (type === 'min') {
      next[0] = { min: value };
    } else {
      next[1] = { max: value };
    }
    setRetailPriceRange(next);
  };

  const clearSelection = () => {
    setAvailableCategories((prev: any[]) => prev.map((c: any) => ({ ...c, isSelected: false })));
    setAvailableSubcategories((prev: any[]) => prev.map((s: any) => ({ ...s, isSelected: false })));
    setAvailableSegments((prev: any[]) => prev.map((s: any) => ({ ...s, isSelected: false })));
    setSelectedBusinessFieldOption(new Set());
    setSelectionOrder([]);
    setRetailPriceRange([{ min: '' }, { max: '' }]);
    setSelectedSegmentParentMap(new Map());
    setSelectedBUParentMap(new Map());
  };

  const handleApplyFilters = () => {
    const filterMap: Record<string, any[]> = {};

    const selectedIds = Array.from(selectedBusinessUnitFieldOption.values());
    if (selectedIds.length > 0) {
      filterMap[FilterKey.BusinessUnit] = selectedIds;
    }

    const selectedCategories = availableCategories.filter((c: any) => c.isSelected);
    const selectedSubcategories = availableSubcategories.filter((s: any) => s.isSelected);
    const selectedSegments = availableSegments.filter((s: any) => s.isSelected);
    if (selectedSubcategories.length > 0) filterMap[FilterKey.Subcategory] = selectedSubcategories;
    if (selectedSegments.length > 0) filterMap[FilterKey.Segment] = selectedSegments;
    if (selectedCategories.length > 0) filterMap[FilterKey.Category] = selectedCategories;

    // Build segment groups respecting OR within a parent BU and AND across different parents.
    if (savedSearchesById && selectedIds.length > 0) {
      // Helpers
      const findNearestTopLevelBU = (nodeId: string): string | null => {
        let parentBU: string | null = null;
        for (const [id, entity] of savedSearchesById.entries()) {
          if (entity?.type === 'businessunit' && (entity.children as any[])?.includes(nodeId)) {
            parentBU = id as string;
            break;
          }
        }
        if (!parentBU) return null;
        let currentId: string = parentBU;
        const visited = new Set<string>();
        while (true) {
          if (visited.has(currentId)) break;
          visited.add(currentId);
          const current = savedSearchesById.get(currentId) as any;
          if (!current) break;
          if (current.isTopLevel) return currentId;
          let nextParent: string | null = null;
          for (const [id, entity] of savedSearchesById.entries()) {
            if (entity?.type === 'businessunit' && (entity.children as any[])?.includes(currentId)) {
              nextParent = id as string;
              break;
            }
          }
          if (!nextParent) return currentId;
          currentId = nextParent;
        }
        return currentId;
      };

      const collectDescendantSegments = (buId: string, result: Set<string>) => {
        const node = savedSearchesById.get(buId) as any;
        if (!node || !node.children) return;
        for (const childId of node.children as string[]) {
          const child = savedSearchesById.get(childId) as any;
          if (!child) continue;
          if (child.type === 'segment') {
            result.add(childId);
          } else if (child.type === 'businessunit') {
            collectDescendantSegments(childId, result);
          }
        }
      };

      const selectedBUIds = selectedIds.filter(
        (id) => (savedSearchesById.get(id as string) as any)?.type === 'businessunit'
      );
      const selectedSegmentIds = selectedIds.filter(
        (id) => (savedSearchesById.get(id as string) as any)?.type === 'segment'
      );

      // Build one group per selected BU (regardless of top-level). Each group = all descendant segments
      const buGroups: string[][] = [];
      for (const buId of selectedBUIds) {
        const set = new Set<string>();
        collectDescendantSegments(buId as string, set);
        const arr = Array.from(set);
        if (arr.length > 0) buGroups.push(arr);
      }

      // For selected segments:
      // - If inside any selected BU, they become their own AND group (additional array)
      // - If outside selected BUs, OR them together by their top-level BU
      const selectedBUSegmentUniverse = new Set<string>(buGroups.flat());
      const explicitSegmentGroups: string[][] = [];
      const outsideSegsByTopLevel = new Map<string, Set<string>>();

      for (const segId of selectedSegmentIds) {
        if (selectedBUSegmentUniverse.has(segId as string)) {
          // Segment selected inside a selected BU: own AND group
          explicitSegmentGroups.push([segId as string]);
        } else {
          // Segment selected outside selected BUs: group by the parent the user clicked (if known)
          const clickedParent = selectedSegmentParentMap.get(segId as string) || null;
          const topLevel = clickedParent || findNearestTopLevelBU(segId as string);
          const key = topLevel || `orphan_${segId}`;
          const set = outsideSegsByTopLevel.get(key) || new Set<string>();
          set.add(segId as string);
          outsideSegsByTopLevel.set(key, set);
        }
      }

      const atlasSegmentGroups: string[][] = [];
      buGroups.forEach((g) => atlasSegmentGroups.push(g));
      explicitSegmentGroups.forEach((g) => atlasSegmentGroups.push(g));
      for (const set of outsideSegsByTopLevel.values()) {
        const arr = Array.from(set);
        if (arr.length > 0) atlasSegmentGroups.push(arr);
      }

      if (atlasSegmentGroups.length > 0) {
        filterMap[FilterKey.SegmentGroups] = atlasSegmentGroups as any;
        // Persist parent maps for correct prefill on reopen
        const segParentPairs = Array.from(selectedSegmentParentMap.entries()).map(([s, p]) => ({ s, p }));
        const buParentPairs = Array.from(selectedBUParentMap.entries()).map(([b, p]) => ({ b, p }));
        if (segParentPairs.length > 0) filterMap[FilterKey.SegmentParentMap] = segParentPairs as any;
        if (buParentPairs.length > 0) filterMap[FilterKey.BusinessUnitParentMap] = buParentPairs as any;
        if (selectionOrder.length > 0) filterMap[FilterKey.SelectionOrder] = selectionOrder as any;
        // Eagerly sync Redux so Doris hooks immediately get the grouping on the next render
        const nextFilters = {
          ...(filters as any),
          businessUnits: selectedIds,
          atlasSegmentGroups,
          atlasSegmentParentMap: segParentPairs,
          atlasBusinessUnitParentMap: buParentPairs,
          atlasSelectionOrder: selectionOrder
        } as any;
        dispatch(filtersActions.updateFilters(nextFilters));
      }
    }

    const cleanedRetailPrice = validMinMaxRetailPrice(retailPriceRange as any);
    if ((cleanedRetailPrice as any).length > 0) {
      filterMap[FilterKey.RetailPrice] = cleanedRetailPrice as any;
    }

    const { generatedFilterParams } = generateFilterUrlString(filterMap);
    const url = new URL(window.location.href);
    url.searchParams.delete('filter');
    const base = `${url.pathname}${url.search}`.replace(/([&?])filter=[^&]*/g, '').replace(/[?&]$/, '');
    const finalUrl = `${base}${generatedFilterParams || ''}`;
    history.push(finalUrl);
    handleClose();
  };

  /**
   * Determines whether the filter should be shown for a given entity type. This is to avoid
   * having category filters show when user is viewing a category already.  Applying this filter
   * would not be correct, so we should not give them the option.
   * @param entityType - The type of entity filter to check.
   * @returns Whether the filter should be shown.
   */
  const shouldShowFilter = (entityType: string) => {
    const currentEntityType = mainEntity.type;

    if (
      entityType === 'category' &&
      ['category', 'subcategory'].includes(currentEntityType) &&
      String(mainEntityId) !== '0'
    ) {
      return false;
    }

    if (entityType === 'subcategory' && currentEntityType === 'subcategory') {
      return false;
    }

    if (entityType === 'segment') {
      return currentEntityType !== 'segment' && segmentOptions.length > 0;
    }

    if (entityType === 'businessunit' && currentEntityType === 'businessunit') {
      return false;
    }

    return true;
  };

  const shouldShowFilteredBusinessUnit = mainEntityType === 'businessunit' && mainEntityId;

  return (
    <>
      <ProductGrowthFilterModalHeader handleClose={handleClose} />
      <div
        className="inner-container"
        style={{ height: '500px', overflow: 'scroll', overflowX: 'hidden', paddingBottom: '35px' }}
      >
        <Flex flexDirection="column" sx={{ paddingLeft: '48px', paddingRight: '43px' }}>
          {shouldShowFilteredBusinessUnit && (
            <>
              <Text variant="subtitle1" sx={{ marginTop: '24px' }}>
                Business Units
              </Text>
              <BusinessUnitFilterGroup
                key={String(mainEntityId)}
                filterTitle={mainEntity?.displayName || ''}
                options={sortBySelectionOrder(
                  (savedSearchesById.get(mainEntityId as string) as any)?.children?.map((child: string) => {
                    const childEntity = savedSearchesById.get(child) as any;
                    const isSeg = childEntity?.type === 'segment';
                    const isBU = childEntity?.type === 'businessunit';
                    const selected = selectedBusinessUnitFieldOption.has(child);
                    const selectedInThisGroup = isSeg
                      ? selected && selectedSegmentParentMap.get(child) === String(mainEntityId)
                      : isBU
                      ? selected && selectedBUParentMap.get(child) === String(mainEntityId)
                      : selected;
                    return { ...childEntity, isSelected: selectedInThisGroup };
                  }) || [],
                  selectionOrder
                )}
                onSelect={onBusinessUnitSelect}
                isRadio={false}
                placeholderText="Search business units"
                selectedBusinessUnitFieldOption={selectedBusinessUnitFieldOption}
                parentTopLevelId={String(mainEntityId)}
              />
            </>
          )}

          {shouldShowFilter('category') ? (
            <FilterGroup
              filterTitle="Categories"
              options={categoryOptions}
              onSelect={onSelectCategory}
              placeholderText="Search categories"
            />
          ) : null}

          {shouldShowFilter('subcategory') ? (
            <FilterGroup
              filterTitle="Subcategories"
              options={subCategoryOptions}
              onSelect={onSelectSubcategory}
              placeholderText="Search subcategories"
            />
          ) : null}

          {shouldShowFilter('businessunit') && topLevelBusinessUnitsOptions.length > 0 ? (
            <>
              <Text variant="subtitle1" sx={{ marginTop: '24px' }}>
                Business Units
              </Text>
              {topLevelBusinessUnitsOptions
                .sort((a: any, b: any) => a.displayName.localeCompare(b.displayName))
                .map((topLevel: any) => {
                  const children = sortBySelectionOrder(
                    (topLevel.children || [])
                      .map((childId: string) => {
                        const entity = savedSearchesById.get(childId) as any;
                        const isSeg = entity?.type === 'segment';
                        const isBU = entity?.type === 'businessunit';
                        const selected = selectedBusinessUnitFieldOption.has(childId);
                        const selectedInThisGroup = isSeg
                          ? selected && selectedSegmentParentMap.get(childId) === String(topLevel.id)
                          : isBU
                          ? selected && selectedBUParentMap.get(childId) === String(topLevel.id)
                          : selected;
                        return { ...entity, isSelected: selectedInThisGroup };
                      })
                      .filter((child: any) => child?.id && child?.displayName),
                    selectionOrder
                  );

                  if (children.length === 0) return null;

                  return (
                    <BusinessUnitFilterGroup
                      key={topLevel.id}
                      filterTitle={topLevel.displayName}
                      options={children as any}
                      onSelect={onBusinessUnitSelect}
                      isRadio={false}
                      placeholderText="Search business units"
                      selectedBusinessUnitFieldOption={selectedBusinessUnitFieldOption}
                      parentTopLevelId={String(topLevel.id)}
                    />
                  );
                })}
            </>
          ) : null}

          {shouldShowFilter('segment') ? (
            <FilterGroup
              filterTitle="Segments"
              options={segmentOptions as any}
              onSelect={onSelectSegment}
              isRadio
              placeholderText="Search segments"
            />
          ) : null}

          <RetailPriceFilterSection
            onChange={(e, type) => onChangeRetailPriceFilter(e, type)}
            retailPriceRange={retailPriceRange}
          />
        </Flex>
      </div>
      <AppFilterModalFooter handleClearFilters={clearSelection} handleApplyFilters={handleApplyFilters} />
    </>
  );
};

export const AtlasFilterModal = ({ open, handleClose, history }: AtlasFilterModalProps) => {
  return (
    <GenericStyledDialogue open={open} onClose={handleClose}>
      <AtlasFilterModalInner handleClose={handleClose} history={history} />
    </GenericStyledDialogue>
  );
};
